import React from "react";

interface UseSelectOptions<T> {
  idKey: keyof T;
  selected?: T[];
  setSelected?: React.Dispatch<React.SetStateAction<T[]>>;
}

function useSelectable<T>(options: UseSelectOptions<T>) {
  const {
    idKey,
    selected: selectedProp,
    setSelected: setSelectedProp,
  } = options;

  const [internalSelected, setInternalSelected] = React.useState<T[]>([]);

  const selected = selectedProp || internalSelected;

  const setSelected = setSelectedProp || setInternalSelected;

  const toggleSelect = React.useCallback(
    (item: T, checked: boolean) => {
      setSelected((prev) => {
        if (checked) {
          return [...prev, item];
        } else {
          return prev.filter((i) => i[idKey] !== item[idKey]);
        }
      });
    },
    [idKey, setSelected]
  );

  const deselectAll = React.useCallback(() => {
    setSelected([]);
  }, [setSelected]);

  const isSelected = React.useCallback(
    (item: T) => {
      return selected.some((i) => i[idKey] === item[idKey]);
    },
    [selected, idKey]
  );

  const removeSelected = React.useCallback(
    (item: T) => {
      setSelected((prev) => {
        return prev.filter((i) => i[idKey] !== item[idKey]);
      });
    },
    [idKey, setSelected]
  );

  const toggleSelectAll = React.useCallback(
    (items: T[], checked: boolean) => {
      if (checked) {
        setSelected((prev) => {
          const next = [...prev];
          items?.forEach((item) => {
            const exist = prev.some((i) => i[idKey] === item[idKey]);
            if (!exist) {
              next.push(item);
            }
          });
          return next;
        });
      } else {
        setSelected((prev) => {
          return prev.filter((i) => {
            return !items.some((item) => i[idKey] === item[idKey]);
          });
        });
      }
    },
    [idKey, setSelected]
  );

  const isAllSelected = React.useCallback(
    (items?: T[]) => {
      if (!items) {
        return false;
      }
      return items.every((item) =>
        selected.some((i) => i[idKey] === item[idKey])
      );
    },
    [selected, idKey]
  );

  const selectedIds = React.useMemo(() => {
    return selected.map((item) => item[idKey] as unknown as string);
  }, [selected, idKey]);

  const selectedCount = selected.length;

  return {
    deselectAll,
    isAllSelected,
    isSelected,
    selected,
    selectedCount,
    selectedIds,
    toggleSelect,
    toggleSelectAll,
    removeSelected,
  };
}

export default useSelectable;
